// Pure rules behind the "Last week → now" block (WeekOverviewPanel.tsx):
// the page's Team / Person / Find filters narrow it to the visible
// initiatives (owner, 2026-10-12: "what we set out to do needs proper
// filtering by team, as does pretty much everything on the page"), so the
// counts are derived here from the filtered rows, never read from the API.

import type { OverviewDelta, OverviewRow, RoadmapOverview, SetOutItem, SetOutOutcome } from '../../services/roadmapReports';

export const DELTA_LABELS: Record<OverviewDelta, string> = {
  finished: 'Finished',
  sub_action_done: 'Sub-action done',
  slipped: 'Target slipped',
  pulled_in: 'Target pulled in',
  dated: 'Got a date',
  blocked: 'Newly blocked',
  unblocked: 'Unblocked',
  dropped: 'Dropped',
  status: 'Status changed',
  next_changed: 'Next step changed',
  new: 'New this week',
  unchanged: 'No change',
};

/** The outcome chip on a "set out to do" line. */
export const OUTCOME_LABELS: Record<SetOutOutcome, string> = {
  done: 'Done',
  still_open: 'Not done yet',
  moved: 'Moved',
  dropped: 'Dropped',
  gone: 'Gone from the roadmap',
};

/** How far back the comparison reaches; the API's since_days. */
export const SINCE_OPTIONS: readonly { days: number; label: string }[] = [
  { days: 0, label: 'Last run' },
  { days: 7, label: 'Last week' },
  { days: 14, label: 'Two weeks' },
];

/** The tiles, in order: each is a delta the table can be narrowed to. */
export const TILE_DELTAS: readonly OverviewDelta[] = ['sub_action_done', 'slipped', 'blocked', 'new', 'unchanged'];
export const TILE_LABELS: Record<string, string> = {
  sub_action_done: 'moved: a sub-action finished',
  slipped: 'target slipped',
  blocked: 'newly blocked',
  new: 'new this week',
  unchanged: 'unchanged',
};

/** Lines shown in "What we set out to do" before the rest fold behind "Show all". */
export const SET_OUT_FOLD = 8;

export interface OverviewSlice {
  rows: OverviewRow[];
  setOut: SetOutItem[];
}

/** The overview narrowed to the initiatives the page's filters keep. `null` keeps everything. */
export function sliceOverview(data: RoadmapOverview, visibleKeys: ReadonlySet<string> | null): OverviewSlice {
  if (!visibleKeys) return { rows: data.rows, setOut: data.set_out };
  return {
    rows: data.rows.filter((r) => visibleKeys.has(r.initiative_key)),
    setOut: data.set_out.filter((s) => visibleKeys.has(s.initiative_key)),
  };
}

/** Rows that are live now: not done, dropped or parked. */
export function activeRows(rows: readonly OverviewRow[]): OverviewRow[] {
  return rows.filter((r) => r.now.status !== 'done' && r.now.status !== 'dropped' && !r.parked);
}

export interface OverviewTally {
  active: number;
  byDelta: Record<OverviewDelta, number>;
  setOut: { total: number; done: number; stillOpen: number; moved: number; dropped: number; gone: number };
}

/** The counts the tiles and headings show, over the active slice. A finished
 * initiative counts as "moved" beside a finished sub-action. */
export function tally(rows: readonly OverviewRow[], setOut: readonly SetOutItem[]): OverviewTally {
  const active = activeRows(rows);
  const byDelta = Object.fromEntries(Object.keys(DELTA_LABELS).map((d) => [d, 0])) as Record<OverviewDelta, number>;
  for (const r of active) for (const d of r.deltas) byDelta[d] += 1;
  byDelta.sub_action_done = active.filter((r) => r.deltas.includes('sub_action_done') || r.deltas.includes('finished')).length;
  const n = (o: SetOutOutcome) => setOut.filter((x) => x.outcome === o).length;
  return {
    active: active.length,
    byDelta,
    setOut: { total: setOut.length, done: n('done'), stillOpen: n('still_open'), moved: n('moved'), dropped: n('dropped'), gone: n('gone') },
  };
}

export interface DoneLine {
  initiative_key: string;
  initiative_title: string;
  /** The sub-action's title; null when the initiative itself finished. */
  title: string | null;
  owner: string | null;
}

/** What got done between the two reports, promised or not (owner, 2026-10-12:
 * the week's wins must not hide behind the list of everything still open).
 * Finished initiatives first, then their sub-actions. Parked / dropped rows
 * are left out; a done initiative is included even though it is no longer active. */
export function doneSince(rows: readonly OverviewRow[]): DoneLine[] {
  const out: DoneLine[] = [];
  for (const r of rows) {
    if (r.parked || r.now.status === 'dropped') continue;
    if (r.deltas.includes('finished')) out.push({ initiative_key: r.initiative_key, initiative_title: r.title, title: null, owner: r.owner });
  }
  for (const r of rows) {
    if (r.parked || r.now.status === 'dropped') continue;
    for (const t of r.actions_done) out.push({ initiative_key: r.initiative_key, initiative_title: r.title, title: t, owner: r.owner });
  }
  return out;
}

const DONE_DELTAS: ReadonlySet<OverviewDelta> = new Set(['finished', 'sub_action_done', 'unchanged']);

/** Does a row match a tile's delta? The "moved" tile also takes a finished initiative. */
export function rowHasDelta(r: OverviewRow, delta: OverviewDelta): boolean {
  if (delta === 'sub_action_done') return r.deltas.includes('sub_action_done') || r.deltas.includes('finished');
  return r.deltas.includes(delta);
}

/** Active rows that changed in some way other than finishing (that is the
 * "Done since" block), then the new ones. */
export function movedRows(rows: readonly OverviewRow[]): { changed: OverviewRow[]; fresh: OverviewRow[] } {
  const active = activeRows(rows);
  return {
    changed: active.filter((r) => r.then && r.deltas.some((d) => !DONE_DELTAS.has(d))),
    fresh: active.filter((r) => r.deltas.includes('new')),
  };
}

const STATUS_WORD: Record<string, string> = {
  proposed: 'Proposed', in_progress: 'In progress', blocked: 'Blocked', waiting: 'Waiting', ongoing: 'Ongoing',
  parked: 'Parked', done: 'Done', dropped: 'Dropped', unknown: 'Unknown',
};

/** One part of the "what moved" line; `action` names the sub-action the part
 * is about (the new next step), so the panel can open it. */
export interface MovedPart {
  text: string;
  action?: string;
}

/** What moved for a row, in the order the deltas came, one part per delta. */
export function movedParts(r: OverviewRow, day: (iso: string | null) => string | null): MovedPart[] {
  if (!r.then) return [];
  const bits: MovedPart[] = [];
  for (const d of r.deltas) {
    if (d === 'sub_action_done' || d === 'finished') continue; // told under "Done since"
    if (d === 'slipped' || d === 'pulled_in') bits.push({ text: `target ${day(r.then.target_date)} → ${day(r.now.target_date)}` });
    else if (d === 'dated') bits.push({ text: `now has a target: ${day(r.now.target_date)}` });
    else if (d === 'next_changed' && r.now.next_action) bits.push({ text: `next step now: ${r.now.next_action}`, action: r.now.next_action });
    else if (d === 'blocked' || d === 'unblocked' || d === 'dropped' || d === 'status') {
      bits.push({ text: `${STATUS_WORD[r.then.status] ?? r.then.status} → ${STATUS_WORD[r.now.status] ?? r.now.status}` });
    }
  }
  return bits;
}

/** One sentence on what moved for a row (movedParts joined). */
export function movedLine(r: OverviewRow, day: (iso: string | null) => string | null): string {
  return movedParts(r, day).map((p) => p.text).join(' · ');
}
